import type {
  CollectionActions,
  CollectionStateSlice,
} from '@freelancer/datastore/core';
import {
  mergeDocuments,
  mergeWebsocketDocuments,
  transformIntoDocuments,
  updateWebsocketDocuments,
} from '@freelancer/datastore/core';
import { UserDocumentSubmissionStatusApi } from 'api-typings/users/users';
import type { UserVerificationProcessesCollection } from '../user-verification-processes';
import { transformUserDocumentSubmission } from './user-document-submissions.transformers';
import type { UserDocumentSubmissionsCollection } from './user-document-submissions.types';
import type { UserVerificationProcessEffectResult } from '../user-verification-processes/user-verification-processes.backend-model';

function isUserVerificationProcessEffectResult(
  result: UserVerificationProcessesCollection['Backend']['Push']['ReturnType'],
): result is UserVerificationProcessEffectResult {
  return 'verification_process_type' in result;
}

export function userDocumentSubmissionsReducer(
  state: CollectionStateSlice<UserDocumentSubmissionsCollection> = {},
  action:
    | CollectionActions<UserDocumentSubmissionsCollection>
    | CollectionActions<UserVerificationProcessesCollection>,
): CollectionStateSlice<UserDocumentSubmissionsCollection> {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'userDocumentSubmissions') {
        const { result, ref, order } = action.payload;

        return mergeDocuments<UserDocumentSubmissionsCollection>(
          state,
          transformIntoDocuments(
            result.user_document_submissions,
            transformUserDocumentSubmission,
          ),
          order,
          ref,
        );
      }
      return state;
    }
    case 'API_PUSH_SUCCESS': {
      if (action.payload.type === 'userDocumentSubmissions') {
        const { ref, result } = action.payload;

        return mergeWebsocketDocuments<UserDocumentSubmissionsCollection>(
          state,
          transformIntoDocuments(
            [result.user_document_submission],
            transformUserDocumentSubmission,
          ),
          ref,
        );
      }

      if (action.payload.type === 'userVerificationProcesses') {
        const { result, ref } = action.payload;

        if (result && isUserVerificationProcessEffectResult(result)) {
          const documentTypeIds = new Set(
            result.verification_process_type.documentTypes.map(
              documentType => documentType.id,
            ),
          );

          const storeSlice = state[ref.path.authUid];

          if (!storeSlice) {
            return state;
          }

          const userDocumentSubmissionsIds = Object.values(storeSlice.documents)
            .filter(userDocumentSubmission =>
              documentTypeIds.has(
                userDocumentSubmission.rawDocument.documentTypeId,
              ),
            )
            .map(
              userDocumentSubmission => userDocumentSubmission.rawDocument.id,
            );

          return updateWebsocketDocuments<UserDocumentSubmissionsCollection>(
            state,
            userDocumentSubmissionsIds,
            userDocumentSubmission => ({
              ...userDocumentSubmission,
              status: UserDocumentSubmissionStatusApi.IN_REVIEW,
            }),
            {
              path: {
                collection: 'userDocumentSubmissions',
                authUid: ref.path.authUid,
              },
            },
          );
        }
      }

      return state;
    }
    default:
      return state;
  }
}
